import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import {
    Box,
    Grid,
    Button,
    Typography,
    TextField,
    Card,
    FormControl,
    FormHelperText,
    useMediaQuery,
    IconButton,
    CircularProgress,
} from '@mui/material';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getCountriesByCoverId } from '../../api';

const Country = ({
    selectedCountries,
    setSelectedCountries,
    rows,
    setRows,
    register,
    errors,
    handleSubmit,
    onSubmit,
    setValue,
    trigger,
    isAddMoreButtonClicked,
    setIsAddMoreButtonClicked,
    travellerData,
}) => {
    const params = useParams();
    const id = params.id;
    const [countryList, setCountryList] = useState([]);
    const [availableCountries, setAvailableCountries] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);

    // Mobile detection
    const isMobile = useMediaQuery('(max-width:600px)');

    // State for yellow boxes and side button
    const [showYellowBoxes, setShowYellowBoxes] = useState(isMobile);
    const [showSideButton, setShowSideButton] = useState(false);

    useEffect(() => {
        // --- UPDATED LOGIC ---
        // Only show the initial pop-up if on mobile AND no countries have been selected yet.
        if (isMobile && selectedCountries.length === 0) {
            setShowYellowBoxes(true);
            setShowSideButton(false);
            const timer = setTimeout(() => {
                setShowYellowBoxes(false);
                setShowSideButton(true);
            }, 2000);
            return () => clearTimeout(timer);
        } else if (isMobile) {
            // If returning to the page on mobile, just show the side button
            setShowYellowBoxes(false);
            setShowSideButton(true);
        } else {
            // Desktop behavior remains unchanged
            setShowYellowBoxes(false);
            setShowSideButton(false);
        }
    }, [isMobile, selectedCountries]); // Added selectedCountries to the dependency array

    // --- NEW HANDLERS FOR PRESS-AND-HOLD FUNCTIONALITY ---
    const handleHoldToShow = () => {
        if (isMobile) {
            setShowYellowBoxes(true);
        }
    };

    const handleReleaseToHide = () => {
        if (isMobile) {
            setShowYellowBoxes(false);
        }
    };

    const handleCountrySelect = async (selectedOption) => {
        if (selectedOption) {
            const newSelectedCountries = [...selectedCountries, selectedOption];
            await setSelectedCountries(newSelectedCountries);
            const availableCountries = countryList.filter((item) => !newSelectedCountries.includes(item));
            setAvailableCountries(availableCountries);
            setValue('countries', newSelectedCountries);
            await trigger('countries');
        }
    };

    const removeCountry = async (country) => {
        const remainingSelectedCountries = selectedCountries.filter((item) => item !== country);
        setSelectedCountries(remainingSelectedCountries);
        setValue('countries', remainingSelectedCountries);
        const availableCountries = countryList.filter((item) => !remainingSelectedCountries.includes(item));
        setAvailableCountries(availableCountries);
    };

    useEffect(() => {
        const fetchCountryList = async () => {
            try {
                // 1. If countries were already provided in travellerData
                const existingCountries = travellerData?.[0]?.geographical_cover?.geo_cover_countries;
                if (existingCountries && existingCountries.length > 0) {
                    const countryListData = existingCountries
                        .map((item) => {
                            const country = item?.Country || item;
                            if (!country) return null;
                            const countryId = country?.country_id || item?.country_id;
                            const countryName = country?.name || item?.name;
                            return {
                                ...country,
                                country_id: countryId,
                                name: countryName,
                                label: countryName,
                                value: countryId,
                            };
                        })
                        .filter(Boolean);
                    setCountryList(countryListData);
                    setAvailableCountries(countryListData);
                    return;
                }

                // 2. Otherwise lazy load countries using cover ID
                const geoCoverId = travellerData?.[0]?.geographical_cover_id || travellerData?.[0]?.geographical_cover?.id;
                if (geoCoverId) {
                    setLoadingCountries(true);
                    const res = await getCountriesByCoverId(geoCoverId);
                    const rawData = res?.data?.data || res?.data?.result || (Array.isArray(res?.data) ? res?.data : []);
                    const countryListData = rawData
                        .map((item) => {
                            const country = item?.Country || item;
                            if (!country) return null;
                            const countryId = country?.country_id || item?.country_id;
                            const countryName = country?.name || item?.name;
                            return {
                                ...country,
                                country_id: countryId,
                                name: countryName,
                                label: countryName,
                                value: countryId,
                            };
                        })
                        .filter(Boolean);

                    setCountryList(countryListData);
                    setAvailableCountries(countryListData);

                    if (travellerData?.[0]) {
                        if (!travellerData[0].geographical_cover) {
                            travellerData[0].geographical_cover = {};
                        }
                        travellerData[0].geographical_cover.geo_cover_countries = rawData;
                    }
                }
            } catch (error) {
                console.error("Error fetching country list:", error);
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountryList();
    }, [id, travellerData]);

    useEffect(() => {
        const availableCountries = countryList.filter((item) => !selectedCountries.includes(item));
        setAvailableCountries(availableCountries);
    }, [selectedCountries, countryList]);

    const addRow = () => {
        setRows([...rows, { date: '', time: '', flight: '' }]);
        setIsAddMoreButtonClicked(true);
    };

    const handleInputChange = (index, field, value) => {
        const newRows = [...rows];
        if (newRows) {
            newRows[index][field] = value;
            setRows(newRows);
        }
    };

    const deleteRow = (index) => {
        if (rows.length === 1 && index === 0) return;
        const newRows = rows.filter((_, i) => i !== index);
        setRows(newRows);
    };

    const minDate = new Date(`${travellerData[0]?.created_at}`).toISOString().split('T')[0];
    const travelEndDate = new Date(`${travellerData[0]?.travel_end_date}`);
    travelEndDate.setDate(travelEndDate.getDate() + 90);
    const maxDate = travelEndDate.toISOString().split('T')[0];

    // --- SX STYLES using your latest values ---
    const polaroidContainerSx = {
        position: 'relative',
        width: '100%',
        height: '400px', // Adjusted height to prevent clipping
    };

    const polaroidOneSx = {
        background: '#fff',
        padding: '8px 8px 20px 8px',
        boxShadow: '3px 3px 8px rgba(0,0,0,0.2)',
        width: '237px',
        position: 'absolute',
        top: '75px',
        left: '15%',
        transform: 'rotate(-12deg)',
        zIndex: 2,
    };

    const polaroidTwoSx = {
        ...polaroidOneSx,
        top: '235px',
        left: 'auto', // Unset left to use right positioning
        right: '15%',
        transform: 'rotate(10deg)',
        zIndex: 1,
    };

    // --- The full-screen blur overlay component for mobile ---
    const MobilePolaroidOverlay = () => (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                bgcolor: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(5px)',
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box sx={polaroidContainerSx}>
                <Card sx={polaroidOneSx}>
                    <Box sx={{ backgroundColor: '#fcc000', p: 1, textAlign: 'center' }}>
                        <Typography fontSize="10px" fontWeight="bold" sx={{ mb: 0 }}>
                            You will instantly receive
                        </Typography>
                        <Box display="flex" justifyContent="center">
                            <Typography fontSize="36px" fontWeight="bold" color="#00239C" sx={{ mt: -1, whiteSpace: 'nowrap' }}>
                                INR 2,000
                            </Typography>
                        </Box>
                        <Typography fontSize="7px" fontWeight="600" sx={{ px: 1, mt: -0.5 }}>
                            If your flight is cancelled. Receive INR 800 if your flight is delayed for more than 3 Hrs, We automatically track your flights.
                        </Typography>
                    </Box>
                </Card>
                <Card sx={polaroidTwoSx}>
                    <Box sx={{ backgroundColor: '#fcc000', p: 1, textAlign: 'center', height: '105px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography fontSize="10px" fontWeight="600" sx={{ marginLeft: '-83px', mt: 1 }}>
                            We will share
                        </Typography>
                        <Typography fontSize="24px" fontWeight="600" color="#00239c" sx={{ mt: -1, marginLeft: '-8px' }}>
                            Destination
                        </Typography>
                        <Typography fontSize="24px" fontWeight="600" color="#00239c" sx={{ marginLeft: '52px', marginTop: '-15px' }}>
                            Discounts
                        </Typography>
                        <Typography fontSize="10px" fontWeight="600" sx={{ ml: 3, mt: -1 }}>
                            With you, where ever possible!
                        </Typography>
                    </Box>
                </Card>
            </Box>
        </Box>
    );

    // --- UPDATED Floating button for mobile with hold/release events ---
    const SideButton = (
        <Box
            sx={{
                position: 'fixed',
                bottom: '30px',
                right: 22,
                zIndex: 2500,
                display: showSideButton ? 'flex' : 'none',
            }}
        >
            <IconButton
                sx={{
                    bgcolor: '#fcc000',
                    color: '#00239c',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    borderRadius: '50%',
                    width: 56,
                    height: 56,
                    '&:hover': { bgcolor: '#ffd700' }
                }}
                // Press and hold events
                onMouseDown={handleHoldToShow}
                onMouseUp={handleReleaseToHide}
                onMouseLeave={handleReleaseToHide} // In case user drags mouse away
                onTouchStart={handleHoldToShow}
                onTouchEnd={handleReleaseToHide}
            >
                <InfoOutlinedIcon />
            </IconButton>
        </Box>
    );

    return (
        <>
            {isMobile && showYellowBoxes && <MobilePolaroidOverlay />}

            {isMobile && SideButton}

            <Box sx={{ mt: 3, py: 1, px: { xs: 2, md: 4 } }}>
                <Grid container spacing={3} sx={{ mt: 3, pb: 5 }}>
                    <Grid item xs={12} md={8}>
                        <Box>
                            <Typography fontSize="14px" sx={{ mb: 5 }}>
                                Now, please tell us about your flight details, so that we can keep you up to date with all travel advisories & advice
                            </Typography>

                            <Grid container sx={{ mb: 1 }}>
                                <Grid item xs={3}>
                                    <Typography fontWeight="bold" sx={{ textAlign: 'left', ml: 1, fontSize: { xs: '10px', sm: '14px' } }}>Date</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography fontWeight="bold" sx={{ textAlign: 'left', ml: 1, fontSize: { xs: '10px', sm: '14px' } }}>Time</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography fontWeight="bold" sx={{ textAlign: 'left', ml: 1, fontSize: { xs: '10px', sm: '14px' } }}>Flight No</Typography>
                                </Grid>
                            </Grid>

                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={3}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            size="small"
                                            inputProps={{
                                                min: minDate,
                                                max: maxDate,
                                                placeholder: "DD/MM/YYYY",
                                                ...register('rows[0].date'),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '20px',
                                                    backgroundColor: rows[0]?.date ? '#00239b' : '#e0e4f3',
                                                    color: rows[0]?.date ? 'white' : 'black',
                                                    height: '48px',
                                                    textAlign: 'center',
                                                    '& fieldset': { borderWidth: '1px' },
                                                },
                                                '& input': { padding: '10px', fontSize: '16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
                                                '& input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 1000px #00239b inset', WebkitTextFillColor: 'white' },
                                                '& input:-webkit-autofill:focus': { WebkitBoxShadow: '0 0 0 1000px #00239b inset', WebkitTextFillColor: 'white' },
                                                '@media (max-width:600px)': { '& input': { fontSize: '8px', padding: '4px' }, '& .MuiOutlinedInput-root': { height: '40px' } },
                                            }}
                                            value={rows[0]?.date}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                handleInputChange(0, 'date', value);
                                                setValue('rows[0].date', value);
                                            }}
                                        />
                                        {errors.rows?.[0]?.date && (
                                            <FormHelperText error>{errors.rows[0].date.message}</FormHelperText>
                                        )}
                                    </Grid>

                                    <Grid item xs={3}>
                                        <TextField
                                            fullWidth
                                            type="time"
                                            size="small"
                                            inputProps={{ ...register(`rows[0].time`) }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '20px',
                                                    backgroundColor: rows[0]?.time ? '#00239b' : '#e0e4f3',
                                                    color: rows[0]?.time ? 'white' : 'black',
                                                    height: '48px',
                                                    textAlign: 'center',
                                                    '& fieldset': { borderWidth: '1px' },
                                                },
                                                '& input': { padding: '10px', fontSize: '16px' },
                                                '& input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 1000px #00239b inset', WebkitTextFillColor: 'white' },
                                                '& input:-webkit-autofill:focus': { WebkitBoxShadow: '0 0 0 1000px #00239b inset', WebkitTextFillColor: 'white' },
                                                '@media (max-width:600px)': { '& input': { fontSize: '9px', padding: '12px' }, '& .MuiOutlinedInput-root': { height: '40px' } },
                                            }}
                                            value={rows[0]?.time}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                handleInputChange(0, 'time', value);
                                                setValue('rows[0].time', value);
                                            }}
                                        />
                                        {errors.rows?.[0]?.time && (
                                            <FormHelperText error>{errors.rows[0].time.message}</FormHelperText>
                                        )}
                                    </Grid>

                                    <Grid item xs={3}>
                                        <TextField
                                            fullWidth
                                            placeholder="Type.."
                                            size="small"
                                            inputProps={{ ...register('rows[0].flight') }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '20px',
                                                    backgroundColor: rows[0]?.flight ? '#00239b' : '#e0e4f3',
                                                    color: rows[0]?.flight ? 'white' : 'black',
                                                    height: '48px',
                                                    textAlign: 'center',
                                                    '& fieldset': { borderWidth: '1px' }
                                                },
                                                '& input': { padding: '10px', fontSize: '16px' },
                                                '& input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 1000px #00239b inset', WebkitTextFillColor: 'white' },
                                                '& input:-webkit-autofill:focus': { WebkitBoxShadow: '0 0 0 1000px #00239b inset', WebkitTextFillColor: 'white' },
                                                '@media (max-width:600px)': { '& input': { fontSize: '9px', padding: '16px' }, '& .MuiOutlinedInput-root': { height: '40px' } },
                                            }}
                                            value={rows[0]?.flight}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                handleInputChange(0, 'flight', value);
                                                setValue('rows[0].flight', value);
                                            }}
                                        />
                                        {errors.rows?.[0]?.flight && (
                                            <FormHelperText error>{errors.rows[0].flight.message}</FormHelperText>
                                        )}
                                    </Grid>

                                    <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mt: 1 }}>
                                        <HighlightOffOutlinedIcon onClick={() => deleteRow(0)} />
                                    </Grid>
                                </Grid>

                                {rows.slice(1).map((row, index) => (
                                    <Grid container spacing={1} key={index + 1} sx={{ mb: 2, mt: 2 }}>
                                        {/* ... (rest of the form rows - no changes here) ... */}
                                    </Grid>
                                ))}
                            </form>

                            <Box sx={{ mt: 2, mb: 4 }}>
                                <Button onClick={addRow} variant="outlined" sx={{ backgroundColor: '#e0e4f3', borderRadius: '20px', borderColor: '#e0e4f3', color: 'black', textTransform: 'none', '&:hover': { backgroundColor: '#d0d4e3', borderColor: '#d0d4e3' } }}>
                                    Add More
                                </Button>
                            </Box>

                            <Box sx={{ mt: 5, mb: 5 }}>
                                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                                    Countries you are going to explore
                                    <sup style={{ fontSize: "x-large", top: 3, left: 3 }} className="text-danger fs-1">*</sup>
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1 }}>
                                    {selectedCountries && selectedCountries.map((country) => (
                                        <Box key={country?.value} sx={{ backgroundColor: "#00239c", height: '40px', color: "#fff", px: 1.5, py: 0.5, borderRadius: '20px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', mr: 2 }}>
                                            <Typography variant="body2">{country?.name}</Typography>
                                            <Typography sx={{ ml: 2, p: 0, color: '#fff', '&:hover': { backgroundColor: 'transparent', cursor: 'pointer' } }} onClick={() => removeCountry(country)}>
                                                ×
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={{ mt: 2, minWidth: '200px', maxWidth: { xs: '100%', sm: '300px' } }}>
                                    <FormControl fullWidth>
                                        <Select
                                            name="countries"
                                            options={availableCountries}
                                            value={null}
                                            onChange={handleCountrySelect}
                                            isClearable={true}
                                            placeholder={loadingCountries ? "Loading countries..." : "Select a country"}
                                            isLoading={loadingCountries}
                                            isDisabled={loadingCountries}
                                            {...register('countries')}
                                            styles={{
                                                control: (base) => ({ ...base, backgroundColor: "#e0e4f3 !important", borderRadius: "25px !important", minHeight: '40px', color: '#000000', zIndex: 999 }),
                                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                                menuList: (base) => ({ ...base, maxHeight: '150px' })
                                            }}
                                        />
                                        {loadingCountries && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, ml: 1 }}>
                                                <CircularProgress size={16} sx={{ color: '#00239c' }} />
                                                <Typography variant="caption" sx={{ color: '#555' }}>
                                                    Loading countries...
                                                </Typography>
                                            </Box>
                                        )}
                                        {errors.countries && (<FormHelperText error>{errors.countries.message}</FormHelperText>)}
                                    </FormControl>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    {/* --- DESKTOP VIEW (Unchanged) --- */}
                    {!isMobile &&
                        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-end' } }}>
                            <Card sx={{ backgroundColor: '#fcc000', width: { xs: '242px', sm: '242px' }, height: '120px', borderRadius: '15px', mb: 3, mt: { xs: 2, md: 4 } }}>
                                <Box sx={{ textAlign: 'center', p: 1, pt: 2 }}>
                                    <Typography fontSize="10px" fontWeight="bold" sx={{ mb: 0 }}>
                                        You will instantly receive
                                    </Typography>
                                    <Typography fontSize="36px" fontWeight="bold" color="#00239C" sx={{ mt: -1 }}>
                                        INR 2,000
                                    </Typography>
                                    <Typography fontSize="7px" fontWeight="600" sx={{ px: 3, mt: -0.5 }}>
                                        If your flight is cancelled. Receive INR 800 if your flight is delayed for more than 3 Hrs, We automatically track your flights.
                                    </Typography>
                                </Box>
                            </Card>
                            <Card sx={{ backgroundColor: '#fcc000', width: { xs: '242px', sm: '242px' }, height: '105px', borderRadius: '15px', mt: { xs: 2, md: 13 } }}>
                                <Box sx={{ textAlign: 'center', p: 1 }}>
                                    <Typography fontSize="10px" fontWeight="600" sx={{ marginLeft: '-83px', mt: 1 }}>
                                        We will share
                                    </Typography>
                                    <Typography fontSize="24px" fontWeight="600" color="#00239c" sx={{ mt: -1, marginLeft: '-8px' }}>
                                        Destination
                                    </Typography>
                                    <Typography fontSize="24px" fontWeight="600" color="#00239c" sx={{ marginLeft: '52px', marginTop: '-15px' }}>
                                        Discounts
                                    </Typography>
                                    <Typography fontSize="10px" fontWeight="600" sx={{ ml: 3, mt: -1 }}>
                                        With you, where ever possible!
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                    }
                </Grid>
            </Box>
        </>
    );
};

export default Country;